import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Alert, Autocomplete, Chip, Divider, Stack, Switch} from "@mui/material";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {LoadingButton} from "@mui/lab";
import {useTranslation} from "react-i18next";
import {useMutation} from "react-query";
import {enqueueSnackbar} from "notistack";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useAllServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import {
    SIMPHONY_TENDER_SERVICE_TOTAL,
    SimphonyCheckWritingRules,
    SimphonyConfig,
    SimphonyCredentials,
    SimphonyDataProcessingRules,
    SimphonyEmployeeLookup,
    SimphonyEmployeeLookupStatus,
    SimphonyLocation,
    SimphonyTenderDefinition
} from "crqlar-react-common/dist/models/integrations/simphony/simphony.config";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import {useState} from "react";
import {
    PeriodicSyncConfig,
    PollerConfig
} from "crqlar-react-common/dist/models/integrations/pollerConfig.model";
import {SimphonyService} from "crqlar-react-common/dist/services/integrations/simphony/simphony.service";
import RevenueCenterMapping from "./RevenueCenterMapping";

interface Props {
    simphonyConfigData: UseEntityData<SimphonyConfig>
}

/**
 * Settings for the Oracle Simphony (STS Gen2) point-of-sale connector.
 *
 * Two pieces of Simphony behaviour are surfaced here rather than hidden, because both stay
 * invisible until they have already cost a property real revenue data:
 *
 *  - the quick-sync period is a **correctness** setting, not a performance one, and
 *  - a "full sync" reconciles; it cannot recover history.
 */
const SimphonyConfigForm = ({simphonyConfigData}: Props) => {

    const {t} = useTranslation()
    const readOnlyUser = useReadOnly()

    const allSectors: Sector[] = useAllSectors()
    const services = useAllServices()

    const simphonyConfig = simphonyConfigData.item
    const nameMissing = isRequiredTextMissing(simphonyConfigData, simphonyConfig.name)
    const setSimphonyConfig = simphonyConfigData.setItem

    // Revenue centers are restaurants, bars and cafes — never accommodation. Fall back to the full
    // list rather than an empty dropdown if a property has not typed its sectors.
    const restaurantSectors = allSectors.filter(s => s.sectorType === SectorType.RESTAURANT)
    const sectorOptions = restaurantSectors.length > 0 ? restaurantSectors : allSectors

    //region Handlers

    const checkConnection = useMutation(
        ['checkSimphonyConfig', simphonyConfig.credentials],
        async () => {
            let success = false
            try {
                if (simphonyConfig.credentials) {
                    await SimphonyService.checkConnection(simphonyConfig)
                    success = true
                }
            } catch (error) {
            }

            enqueueSnackbar(
                success
                    ? t('Connection successful')
                    : t('Connection failed. Check the credentials, and whether the API account password has expired — Simphony expires it every 60 days.'),
                {
                    variant: success ? 'success' : 'error',
                    anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
                }
            )
        }
    )

    const updateField = (field: keyof SimphonyConfig, value: any) => {
        setSimphonyConfig({...simphonyConfig, [field]: value})
    }

    const updateCredentialsField = (field: keyof SimphonyCredentials, value: any) => {
        setSimphonyConfig({
            ...simphonyConfig,
            credentials: {...simphonyConfig.credentials, [field]: value}
        })
    }

    const updatePollerConfig = (field: keyof PollerConfig, value: any) => {
        setSimphonyConfig({
            ...simphonyConfig,
            pollerConfig: {...simphonyConfig.pollerConfig, [field]: value}
        })
    }

    const updateDailyConfig = (field: keyof PeriodicSyncConfig, value: any) => {
        updatePollerConfig('daySync', {...simphonyConfig.pollerConfig?.daySync, [field]: value})
    }

    const updateQuickConfig = (field: keyof PeriodicSyncConfig, value: any) => {
        updatePollerConfig('quickSync', {...simphonyConfig.pollerConfig?.quickSync, [field]: value})
    }

    const updateDataProcessingRules = (field: keyof SimphonyDataProcessingRules, value: any) => {
        updateField('dataProcessingRules', {...simphonyConfig.dataProcessingRules, [field]: value})
    }

    const updateCheckWriting = (field: keyof SimphonyCheckWritingRules, value: any) => {
        updateField('checkWriting', {...simphonyConfig.checkWriting, [field]: value})
    }

    // Blank clears the id rather than storing NaN — the backend refuses to write until all three are set.
    const parseRef = (text: string): number | undefined => {
        const parsed = parseInt(text)
        return Number.isNaN(parsed) ? undefined : parsed
    }

    //endregion Handlers

    //region UI

    const quickSyncMinutes = simphonyConfig.pollerConfig?.quickSync?.syncPeriodMinutes

    // Server-owned: set by the sync the first time it tries to read a closed check by number.
    const closedCheckRecoverySupported = simphonyConfig.syncState?.closedCheckRecoverySupported

    // Oracle's ISV Readiness Checklist asks integrators to read the hierarchy rather than have a
    // customer type location codes by hand. Loaded on demand rather than on mount: it needs working
    // credentials, and on a new connector those are still being typed in as the screen opens.
    const [locations, setLocations] = useState<SimphonyLocation[]>([])

    const loadLocations = useMutation(
        ['simphonyLocations', simphonyConfig.id],
        async () => setLocations(await SimphonyService.listLocations(simphonyConfig))
    )

    const selectedLocation = locations.find(l => l.locRef === simphonyConfig.credentials?.locRef) ?? null

    const credentialsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Integration name')}
                    tooltip={t('Internal label for identifying this Simphony integration configuration.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={simphonyConfig.name || ''}
                        error={nameMissing}
                        fullWidth
                        onChange={e => updateField('name', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Authentication URL')}
                    tooltip={t('OIDC provider base, ending in /oidc-provider/v1/oauth2')}
                >
                    <TextField
                        id={'auth-base-url'}
                        size={'small'}
                        value={simphonyConfig.credentials?.authBaseUrl || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('authBaseUrl', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('API URL')}
                    tooltip={t('Simphony Transaction Services Gen2 base, ending in /api/v1. This differs per customer — do not copy it from another property.')}
                >
                    <TextField
                        id={'api-base-url'}
                        size={'small'}
                        value={simphonyConfig.credentials?.apiBaseUrl || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('apiBaseUrl', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Client ID')}
                    tooltip={t('OIDC client ID issued by Oracle for this API account.')}
                >
                    <TextField
                        id={'client-id'}
                        size={'small'}
                        value={simphonyConfig.credentials?.clientId || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('clientId', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Organization short name')}
                    tooltip={t('Simphony enterprise short name, exactly as issued by Oracle, for example OPN. Case matters for sign-in.')}
                >
                    <TextField
                        id={'org-short-name'}
                        size={'small'}
                        value={simphonyConfig.credentials?.orgShortName || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('orgShortName', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Location')}
                    tooltip={t('The property this connection reads from. Load the list to pick it rather than typing the code — a mistyped location reference looks exactly like a property with no checks.')}
                >
                    <Stack gap={1} width={'100%'}>
                        <Stack direction={'row'} gap={1}>
                            <Autocomplete
                                id={'loc-ref'}
                                freeSolo
                                options={locations}
                                getOptionLabel={(option: SimphonyLocation | string) =>
                                    typeof option === 'string'
                                        ? option
                                        : `${option.locRef} \u2014 ${option.name || ''}`.trim()}
                                value={selectedLocation ?? (simphonyConfig.credentials?.locRef || '')}
                                fullWidth
                                onChange={(_, option) => updateCredentialsField(
                                    'locRef',
                                    typeof option === 'string' ? option : option?.locRef || ''
                                )}
                                // freeSolo keeps manual entry working: a property whose credentials
                                // are not yet saved cannot load the list, and Oracle's own lab has
                                // needed a code typed by hand before now.
                                onInputChange={(_, text, reason) => {
                                    if (reason === 'input') updateCredentialsField('locRef', text)
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} size={'small'} fullWidth
                                               placeholder={'P43005'}/>
                                )}
                            />

                            <Button
                                id={'load-locations'}
                                variant={'outlined'}
                                size={'small'}
                                disabled={loadLocations.isLoading}
                                onClick={() => loadLocations.mutate()}
                                sx={{whiteSpace: 'nowrap'}}
                            >
                                {loadLocations.isLoading
                                    ? <CircularProgress size={18}/>
                                    : t('Load from Simphony')}
                            </Button>
                        </Stack>

                        {loadLocations.isSuccess && locations.length === 0 && (
                            <Alert variant={'outlined'} severity={'warning'} sx={{py: 0, textAlign: 'start'}}>
                                {t('Simphony returned no properties for this account. Check the credentials and organization short name above — the API only returns properties assigned to your API account.')}
                            </Alert>
                        )}
                    </Stack>
                </Property>

                <Property
                    title={t('Username')}
                    tooltip={t('Simphony API account username.')}
                >
                    <TextField
                        id={'username'}
                        size={'small'}
                        value={simphonyConfig.credentials?.username || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('username', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Password')}
                    tooltip={t('Leave blank to keep the stored password. Simphony expires API account passwords every 60 days; an expired password blocks a fresh sign-in but does not stop an already-running connector.')}
                >
                    <TextField
                        id={'password'}
                        size={'small'}
                        type={'password'}
                        autoComplete={'new-password'}
                        placeholder={simphonyConfig.id ? t('Unchanged') : ''}
                        value={simphonyConfig.credentials?.password || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('password', e.target.value)}
                    />
                </Property>

                <Stack direction={'row'} justifyContent={'flex-end'}>
                    <LoadingButton
                        id={'check-connection'}
                        variant={'outlined'}
                        loading={checkConnection.isLoading}
                        disabled={readOnlyUser}
                        onClick={() => checkConnection.mutate()}
                    >
                        {t('Test connection')}
                    </LoadingButton>
                </Stack>

                <Alert variant={'outlined'} severity={'info'} sx={{textAlign: 'start'}}>
                    {t('Test connection checks the credentials only. It does not require a POS workstation to be switched on, so this configuration can be saved outside service hours.')}
                </Alert>
            </Stack>
        </Stack>
    )

    const pollerSection = (
        <AdditionalProperties title={t('Sync settings')} variant={'outlined'}>

            {/*
              * Two different warnings, because the situation is genuinely different depending on
              * what the property's Simphony will serve. Showing the pessimistic one to a property
              * where recovery works would make an operator shorten the sync period for no reason;
              * showing the optimistic one where it does not would hide missing revenue.
              */}
            {closedCheckRecoverySupported === false ? (
                <Alert variant={'outlined'} severity={'error'} sx={{textAlign: 'start', mb: 1}}>
                    {t('This Simphony version will not return checks that were paid without being sent to Orders, so those sales are not captured at all. Keep the quick sync period below two minutes, ask staff to send checks to Orders before taking payment, and contact support about Business Intelligence API access.')}
                </Alert>
            ) : (
                <Alert variant={'outlined'} severity={'warning'} sx={{textAlign: 'start', mb: 1}}>
                    {t('Simphony only lists checks that are currently open. A check opened and paid between two quick syncs is not seen live — the connector goes back for it afterwards by check number, but that only works where Simphony will serve closed checks. At counter-service venues keep the quick sync period below two minutes.')}
                </Alert>
            )}

            <Typography textAlign={'start'}>
                {t('Quick sync')}
            </Typography>

            <Property
                title={t('Enabled')}
                tooltip={t('Turns the scheduled quick sync on or off. This is the pass that discovers new checks.')}
            >
                <Switch
                    id={'quick-sync-enabled'}
                    checked={simphonyConfig.pollerConfig?.quickSync?.enabled || false}
                    onChange={e => updateQuickConfig('enabled', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Sync period minutes')}
                tooltip={t('Minutes between quick sync runs. This is a correctness setting, not a performance one — see the warning above.')}
            >
                <Stack gap={1} width={'100%'}>
                    <TextField
                        id={'quick-sync-period-minutes'}
                        size={'small'}
                        type={'number'}
                        value={quickSyncMinutes ?? ''}
                        fullWidth
                        onChange={e => updateQuickConfig('syncPeriodMinutes', parseInt(e.target.value))}
                    />
                    {!!quickSyncMinutes && quickSyncMinutes > 2 && (
                        <Alert variant={'outlined'} severity={'warning'} sx={{py: 0, textAlign: 'start'}}>
                            {closedCheckRecoverySupported === false
                                ? t('Checks paid within {{minutes}} minutes of being opened are missed.', {minutes: quickSyncMinutes})
                                : t('Checks paid within {{minutes}} minutes of being opened are not seen live and have to be recovered afterwards.', {minutes: quickSyncMinutes})}
                        </Alert>
                    )}
                </Stack>
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a quick sync job is treated as stale.')}
            >
                <TextField
                    id={'quick-stale-timeout-minutes'}
                    size={'small'}
                    type={'number'}
                    value={simphonyConfig.pollerConfig?.quickSync?.staleTimeoutMinutes || ''}
                    fullWidth
                    onChange={e => updateQuickConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                />
            </Property>

            <Typography textAlign={'start'} sx={{mt: 2}}>
                {t('Daily sync')}
            </Typography>

            <Property
                title={t('Enabled')}
                tooltip={t('Re-reads checks opened days ago that have only now been settled, which the quick sync has stopped following.')}
            >
                <Switch
                    id={'daily-sync-enabled'}
                    checked={simphonyConfig.pollerConfig?.daySync?.enabled || false}
                    onChange={e => updateDailyConfig('enabled', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a daily sync job is treated as stale.')}
            >
                <TextField
                    id={'daily-stale-timeout-minutes'}
                    size={'small'}
                    type={'number'}
                    value={simphonyConfig.pollerConfig?.daySync?.staleTimeoutMinutes || ''}
                    fullWidth
                    onChange={e => updateDailyConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                />
            </Property>
        </AdditionalProperties>
    )

    // Always visible, never inside a collapsed accordion: an empty mapping means nothing syncs, so
    // this is the one setting nobody may overlook.
    const revenueCentersSection = (
        <RevenueCenterMapping
            value={simphonyConfig.dataProcessingRules?.rvcRefToSectorId || {}}
            onChange={value => updateDataProcessingRules('rvcRefToSectorId', value)}
            sectors={sectorOptions}
            configId={simphonyConfig.id}
            config={simphonyConfig}
            readOnly={readOnlyUser}
        />
    )

    const checkWriting = simphonyConfig.checkWriting
    const checkWritingIncomplete = !!checkWriting?.enabled && (
        checkWriting.checkEmployeeRef == null ||
        checkWriting.orderTypeRef == null ||
        checkWriting.serviceTotalTenderId == null
    )

    // Simphony's tenders (CONFG-5), loaded on demand like the location list. Only Service Total
    // tenders can carry crqlar's zero-amount round; "Service" is offered first because the others
    // (Print, Send, Fire, ...) may also act on the POS.
    const [tenders, setTenders] = useState<SimphonyTenderDefinition[]>([])

    const loadTenders = useMutation(
        ['simphonyTenders', simphonyConfig.id],
        async () => setTenders(await SimphonyService.listTenders(simphonyConfig))
    )

    const serviceTotalTenders = tenders
        .filter(tender => tender.type === SIMPHONY_TENDER_SERVICE_TOTAL)
        .sort((a, b) => Number(b.name === 'Service') - Number(a.name === 'Service'))

    const selectedTender = tenders.find(tender => tender.tenderId === checkWriting?.serviceTotalTenderId)
    const selectedTenderNotServiceTotal = !!selectedTender && selectedTender.type !== SIMPHONY_TENDER_SERVICE_TOTAL

    const tenderLabel = (tender: SimphonyTenderDefinition) => `${tender.tenderId} \u2014 ${tender.name || ''}`.trim()

    // Simphony can only look an employee up by number (EMP-1) and answers with the number alone, so
    // the employee is verified, not picked. The result is kept with the number it was for, so editing
    // the field hides a stale answer.
    const [employeeLookup, setEmployeeLookup] = useState<SimphonyEmployeeLookup>()

    const verifyEmployee = useMutation(
        ['simphonyEmployee', simphonyConfig.id],
        async (employeeRef: number) => setEmployeeLookup(await SimphonyService.findEmployee(simphonyConfig, employeeRef)),
        {onError: () => setEmployeeLookup(undefined)}
    )

    const currentEmployeeLookup = employeeLookup?.employeeRef === checkWriting?.checkEmployeeRef
        ? employeeLookup
        : undefined

    const checkWritingRefField = (
        id: string,
        title: string,
        tooltip: string,
        field: 'checkEmployeeRef' | 'orderTypeRef' | 'serviceTotalTenderId',
        placeholder: string
    ) => (
        <Property title={title} tooltip={tooltip}>
            <TextField
                id={id}
                size={'small'}
                type={'number'}
                placeholder={placeholder}
                value={checkWriting?.[field] ?? ''}
                disabled={readOnlyUser}
                fullWidth
                onChange={e => updateCheckWriting(field, parseRef(e.target.value))}
            />
        </Property>
    )

    const checkWritingSection = (
        <AdditionalProperties title={t('Reservation on the POS check')} variant={'outlined'}>
            <Alert variant={'outlined'} severity={'info'} sx={{textAlign: 'start', mb: 1}}>
                {t('When a reservation on a table bound in the Tables tab is set to Active, crqlar writes the reservation onto that table\'s check: it labels the check a waiter has already opened, or opens an empty one. The guest\'s spend is then linked for certain instead of matched by table and time. Staff must open a check crqlar opened from Orders → My Orders — “Begin Check by Table” starts a second check.')}
            </Alert>

            <Property
                title={t('Enabled')}
                tooltip={t('Turns writing reservations onto POS checks on or off.')}
            >
                <Switch
                    id={'check-writing-enabled'}
                    checked={checkWriting?.enabled || false}
                    disabled={readOnlyUser}
                    onChange={e => updateCheckWriting('enabled', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Check employee number')}
                tooltip={t('The Simphony employee a crqlar-opened check belongs to. Create a dedicated employee for crqlar in EMC, then verify the number here.')}
            >
                <Stack gap={1} width={'100%'}>
                    <Stack direction={'row'} gap={1}>
                        <TextField
                            id={'check-writing-employee'}
                            size={'small'}
                            type={'number'}
                            placeholder={'2626'}
                            value={checkWriting?.checkEmployeeRef ?? ''}
                            disabled={readOnlyUser}
                            fullWidth
                            onChange={e => updateCheckWriting('checkEmployeeRef', parseRef(e.target.value))}
                        />

                        <Button
                            id={'verify-employee'}
                            variant={'outlined'}
                            size={'small'}
                            disabled={verifyEmployee.isLoading || checkWriting?.checkEmployeeRef == null}
                            onClick={() => verifyEmployee.mutate(checkWriting!.checkEmployeeRef!)}
                            sx={{whiteSpace: 'nowrap'}}
                        >
                            {verifyEmployee.isLoading
                                ? <CircularProgress size={18}/>
                                : t('Verify in Simphony')}
                        </Button>
                    </Stack>

                    {currentEmployeeLookup?.status === SimphonyEmployeeLookupStatus.FOUND && (
                        <Alert variant={'outlined'} severity={'success'} sx={{py: 0, textAlign: 'start'}}>
                            {t('Employee {{number}} exists in Simphony.', {number: currentEmployeeLookup.employeeRef})}
                        </Alert>
                    )}

                    {currentEmployeeLookup?.status === SimphonyEmployeeLookupStatus.NOT_FOUND && (
                        <Alert variant={'outlined'} severity={'error'} sx={{py: 0, textAlign: 'start'}}>
                            {t('Simphony has no employee {{number}} at this location. Check the number in EMC.', {number: currentEmployeeLookup.employeeRef})}
                        </Alert>
                    )}

                    {(verifyEmployee.isError || currentEmployeeLookup?.status === SimphonyEmployeeLookupStatus.ERROR) && (
                        <Alert variant={'outlined'} severity={'warning'} sx={{py: 0, textAlign: 'start'}}>
                            {t('Could not verify the employee. Check the credentials and location above.')}
                            {currentEmployeeLookup?.error ? ` (${currentEmployeeLookup.error})` : ''}
                        </Alert>
                    )}
                </Stack>
            </Property>

            {checkWritingRefField(
                'check-writing-order-type',
                t('Order type number'),
                t('Order type of a crqlar-opened check, for example Dine In. A check a waiter opened keeps its own.'),
                'orderTypeRef',
                '1'
            )}

            <Property
                title={t('Service Total tender')}
                tooltip={t('A tender of type Service Total. Simphony refuses a check without items unless it carries a tender; it is posted at zero and does not stay on the check.')}
            >
                <Stack gap={1} width={'100%'}>
                    <Stack direction={'row'} gap={1}>
                        <Autocomplete
                            id={'check-writing-tender'}
                            freeSolo
                            options={serviceTotalTenders}
                            getOptionLabel={(option: SimphonyTenderDefinition | string) =>
                                typeof option === 'string' ? option : tenderLabel(option)}
                            value={selectedTender ?? (checkWriting?.serviceTotalTenderId?.toString() || '')}
                            disabled={readOnlyUser}
                            fullWidth
                            onChange={(_, option) => updateCheckWriting(
                                'serviceTotalTenderId',
                                typeof option === 'string' ? parseRef(option) : option?.tenderId
                            )}
                            // freeSolo keeps typing the number working before credentials are saved.
                            onInputChange={(_, text, reason) => {
                                if (reason === 'input') updateCheckWriting('serviceTotalTenderId', parseRef(text))
                            }}
                            renderInput={(params) => (
                                <TextField {...params} size={'small'} fullWidth placeholder={'901'}/>
                            )}
                        />

                        <Button
                            id={'load-tenders'}
                            variant={'outlined'}
                            size={'small'}
                            disabled={loadTenders.isLoading || readOnlyUser}
                            onClick={() => loadTenders.mutate()}
                            sx={{whiteSpace: 'nowrap'}}
                        >
                            {loadTenders.isLoading
                                ? <CircularProgress size={18}/>
                                : t('Load from Simphony')}
                        </Button>
                    </Stack>

                    {loadTenders.isSuccess && serviceTotalTenders.length === 0 && (
                        <Alert variant={'outlined'} severity={'warning'} sx={{py: 0, textAlign: 'start'}}>
                            {t('Simphony returned no Service Total tenders. Check the credentials, the location and that at least one revenue center is mapped.')}
                        </Alert>
                    )}

                    {selectedTenderNotServiceTotal && (
                        <Alert variant={'outlined'} severity={'error'} sx={{py: 0, textAlign: 'start'}}>
                            {t('Tender {{tender}} is a payment tender, not Service Total. Simphony will refuse crqlar\'s checks with it.', {tender: tenderLabel(selectedTender!)})}
                        </Alert>
                    )}

                    {serviceTotalTenders.length > 0 && (
                        <Alert variant={'outlined'} severity={'info'} sx={{py: 0, textAlign: 'start'}}>
                            {t('Choose the plain “Service” tender. Other Service Total tenders such as Print, Send or Fire may also print or send the check on the POS.')}
                        </Alert>
                    )}
                </Stack>
            </Property>

            {checkWritingIncomplete && (
                <Alert variant={'outlined'} severity={'warning'} sx={{py: 0, textAlign: 'start'}}>
                    {t('Nothing is written to checks until the employee, order type and tender numbers are all set.')}
                </Alert>
            )}
        </AdditionalProperties>
    )

    const dataProcessingSection = (
        <AdditionalProperties title={t('Data processing rules')} variant={'outlined'}>

            <Property
                title={t('Excluded revenue centers')}
                tooltip={t('Revenue centers to skip entirely, for example a staff canteen. Type the revenue center number and press Enter.')}
            >
                <Autocomplete
                    id={'excluded-rvc-refs'}
                    multiple
                    freeSolo
                    options={[] as string[]}
                    value={simphonyConfig.dataProcessingRules?.excludedRvcRefs || []}
                    disabled={readOnlyUser}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip size={'small'} label={option} {...getTagProps({index})} key={index}/>
                        ))
                    }
                    renderInput={(params) => <TextField {...params} size={'small'} fullWidth/>}
                    fullWidth
                    onChange={(e, value) => updateDataProcessingRules('excludedRvcRefs', value)}
                />
            </Property>

            <Property
                title={t('Already billed through the PMS')}
                tooltip={t('Revenue centers whose checks also reach crqlar through the property folio, for example a restaurant where most dinners are charged to the room. These checks are still recorded, but left out of revenue totals so guest spend is not counted twice.')}
            >
                <Autocomplete
                    id={'pms-billed-rvc-refs'}
                    multiple
                    freeSolo
                    options={[] as string[]}
                    value={simphonyConfig.dataProcessingRules?.pmsBilledRvcRefs || []}
                    disabled={readOnlyUser}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip size={'small'} label={option} {...getTagProps({index})} key={index}/>
                        ))
                    }
                    renderInput={(params) => <TextField {...params} size={'small'} fullWidth/>}
                    fullWidth
                    onChange={(e, value) => updateDataProcessingRules('pmsBilledRvcRefs', value)}
                />
            </Property>

            <Property
                title={t('Default service')}
                tooltip={t('Service assigned to menu items that have no explicit mapping.')}
            >
                <Autocomplete
                    id={'default-service'}
                    options={services}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={services.find((s: any) => s.id === simphonyConfig.dataProcessingRules?.defaultServiceId) || null}
                    disabled={readOnlyUser}
                    renderInput={(params) => <TextField {...params} size={'small'} fullWidth/>}
                    fullWidth
                    onChange={(e, value: any) => updateDataProcessingRules('defaultServiceId', value?.id || undefined)}
                />
            </Property>
        </AdditionalProperties>
    )

    const configurationSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Property id')}
                    tooltip={t('Stamped onto all data imported through this connector.')}
                >
                    <Stack gap={1} width={'100%'}>
                        <Alert variant={'outlined'} severity={'error'} sx={{py: 0, textAlign: 'start'}}>
                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
                            id={'property-id'}
                            size={'small'}
                            value={simphonyConfig.propertyId || ''}
                            fullWidth
                            onChange={e => updateField('propertyId', e.target.value)}
                        />
                    </Stack>
                </Property>

                {pollerSection}
                {dataProcessingSection}
                {checkWritingSection}
            </Stack>
        </Stack>
    )

    //endregion UI

    return (
        <Stack sx={{width: '100%', gap: 6}}>
            {credentialsSection}
            <Divider/>
            {revenueCentersSection}
            <Divider/>
            {configurationSection}
        </Stack>
    )
}

export default SimphonyConfigForm;
